import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import ReactFlow, {
  useNodesState,
  useEdgesState,
  Background,
  Controls,
  MarkerType,
  ReactFlowProvider,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { fetchDocumentNetwork, fetchDocumentCommonality, fetchPdfList } from '../services/dashboardService';

// ── Shared Constants & Helpers ─────────────────────────────────────────────

const ENTITY_COLORS = {
  COMPANY: '#3b82f6',
  PERSON: '#22c55e',
  PROPERTY: '#f59e0b',
  LEGAL_CASE: '#ef4444',
};
const entityColor = (type) => ENTITY_COLORS[type] || '#9ca3af';

const getProviderColor = (provider) => {
  if (!provider) return '#6366f1';
  let hash = 0;
  for (let i = 0; i < provider.length; i++) hash = provider.charCodeAt(i) + ((hash << 5) - hash);
  return `hsl(${Math.abs(hash) % 360}, 55%, 50%)`;
};

const extractFileName = (filePath) => {
  if (!filePath) return null;
  const parts = filePath.replace(/\\/g, '/').split('/');
  return parts[parts.length - 1] || filePath;
};

const getNodeDisplayName = (n) => {
  const raw = n.label || n.file_name || `PDF ${n.id}`;
  if (raw.includes('/') || raw.includes('\\')) return extractFileName(raw);
  return raw;
};

const getPdfDisplayName = (d) => {
  if (!d) return 'Unknown';
  const raw = d.file_name || d.title || `PDF #${d.id}`;
  if (raw.includes('/') || raw.includes('\\')) return extractFileName(raw);
  return raw;
};

const formatDate = (d) => {
  if (!d) return null;
  try {
    return new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch { return null; }
};

// ── Provider Grouped Layout ────────────────────────────────────────────────

const buildProviderGroupedLayout = (apiNodes) => {
  // Group nodes by provider
  const groups = {};
  apiNodes.forEach(n => {
    const p = n.provider || 'Unknown';
    if (!groups[p]) groups[p] = [];
    groups[p].push(n);
  });

  const providers = Object.keys(groups);
  const clusterCols = Math.max(1, Math.ceil(Math.sqrt(providers.length)));
  const CLUSTER_SPACING_X = 620;
  const CLUSTER_SPACING_Y = 520;

  const posMap = {};
  providers.forEach((provider, pIdx) => {
    const cx = (pIdx % clusterCols) * CLUSTER_SPACING_X + CLUSTER_SPACING_X / 2;
    const cy = Math.floor(pIdx / clusterCols) * CLUSTER_SPACING_Y + CLUSTER_SPACING_Y / 2;
    const nodes = groups[provider];
    const radius = Math.max(110, nodes.length * 32);

    nodes.forEach((n, nIdx) => {
      const angle = (nIdx / nodes.length) * 2 * Math.PI - Math.PI / 2;
      posMap[String(n.id)] = {
        x: cx + radius * Math.cos(angle),
        y: cy + radius * Math.sin(angle),
      };
    });
  });

  return posMap;
};

// ── Latent Connection Computation (2-hop graph analysis) ───────────────────

const computeLatentConnections = (nodeId, allEdges, allNodes) => {
  // Collect direct neighbors of the selected node
  const directNeighborIds = new Set();
  allEdges.forEach(e => {
    if (e.source === nodeId) directNeighborIds.add(e.target);
    if (e.target === nodeId) directNeighborIds.add(e.source);
  });

  const results = [];

  allNodes.forEach(n => {
    // Skip the node itself and its direct neighbors
    if (n.id === nodeId || directNeighborIds.has(n.id)) return;

    // Build this candidate's neighbor set
    const candidateNeighbors = new Set();
    allEdges.forEach(e => {
      if (e.source === n.id) candidateNeighbors.add(e.target);
      if (e.target === n.id) candidateNeighbors.add(e.source);
    });

    // Find shared intermediary nodes (2-hop paths)
    const sharedIntermediaries = [...directNeighborIds].filter(id => candidateNeighbors.has(id));

    if (sharedIntermediaries.length > 0) {
      results.push({
        node: n,
        sharedIntermediaryCount: sharedIntermediaries.length,
        sharedIntermediaryNames: sharedIntermediaries.map(
          id => allNodes.find(x => x.id === id)?.data?.fullLabel || allNodes.find(x => x.id === id)?.data?.label || id
        ),
      });
    }
  });

  return results.sort((a, b) => b.sharedIntermediaryCount - a.sharedIntermediaryCount).slice(0, 6);
};

// ── Provider Overlap Computation ───────────────────────────────────────────

const computeProviderOverlap = (rfNodes, rfEdges) => {
  const nodeProviderMap = {};
  rfNodes.forEach(n => { nodeProviderMap[n.id] = n.data?.provider || 'Unknown'; });

  const providerStats = {};
  rfNodes.forEach(n => {
    const p = n.data?.provider || 'Unknown';
    if (!providerStats[p]) {
      providerStats[p] = { docCount: 0, crossConnections: 0, intraConnections: 0, color: getProviderColor(p) };
    }
    providerStats[p].docCount++;
  });

  const pairMap = {};
  let maxPairCount = 0;

  rfEdges.forEach(e => {
    const pSrc = nodeProviderMap[e.source] || 'Unknown';
    const pTgt = nodeProviderMap[e.target] || 'Unknown';

    if (pSrc === pTgt) {
      if (providerStats[pSrc]) providerStats[pSrc].intraConnections++;
    } else {
      const key = [pSrc, pTgt].sort().join('|||');
      pairMap[key] = (pairMap[key] || 0) + (e.data?.shared_entity_count || 1);
      if (pairMap[key] > maxPairCount) maxPairCount = pairMap[key];
      if (providerStats[pSrc]) providerStats[pSrc].crossConnections++;
      if (providerStats[pTgt]) providerStats[pTgt].crossConnections++;
    }
  });

  const pairs = Object.entries(pairMap)
    .map(([key, count]) => {
      const [p1, p2] = key.split('|||');
      return { p1, p2, count };
    })
    .sort((a, b) => b.count - a.count);

  return { providerStats, pairs, maxPairCount };
};

// ── Graph Builders ─────────────────────────────────────────────────────────

const buildNetworkElements = (apiNodes, apiEdges, groupByProvider = false) => {
  const degreeMap = {};
  apiEdges.forEach(e => {
    degreeMap[String(e.source)] = (degreeMap[String(e.source)] || 0) + 1;
    degreeMap[String(e.target)] = (degreeMap[String(e.target)] || 0) + 1;
  });

  const posMap = groupByProvider ? buildProviderGroupedLayout(apiNodes) : null;
  const cols = Math.ceil(Math.sqrt(apiNodes.length)) || 1;

  const rfNodes = apiNodes.map((n, idx) => {
    const degree = degreeMap[String(n.id)] || 0;
    const size = 32 + degree * 8;
    const color = getProviderColor(n.provider);
    const displayName = getNodeDisplayName(n);
    const shortLabel = displayName.length > 20 ? displayName.slice(0, 18) + '…' : displayName;

    const position = posMap
      ? (posMap[String(n.id)] || { x: (idx % cols) * 280, y: Math.floor(idx / cols) * 200 })
      : { x: (idx % cols) * 280, y: Math.floor(idx / cols) * 200 };

    return {
      id: String(n.id),
      position,
      data: {
        label: shortLabel,
        provider: n.provider,
        degree,
        fullLabel: displayName,
        description: n.description || n.summary || null,
        createdDate: n.created_date || n.upload_date || null,
        processingStatus: n.processing_status || null,
        totalEdges: n.total_edges_contributed ?? null,
        sourceType: n.source_type || 'pdf',
        url: n.url || null,
        originalData: n,
      },
      style: {
        background: color,
        color: '#fff',
        border: groupByProvider ? `2px solid rgba(255,255,255,0.4)` : 'none',
        borderRadius: 8,
        fontSize: 11,
        fontWeight: 600,
        padding: '8px 12px',
        width: Math.max(size + 60, 140),
        textAlign: 'center',
        boxShadow: '0 4px 12px rgba(0,0,0,.15)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
      },
    };
  });

  const rfEdges = apiEdges.map(e => {
    const srcProvider = rfNodes.find(n => n.id === String(e.source))?.data?.provider;
    const tgtProvider = rfNodes.find(n => n.id === String(e.target))?.data?.provider;
    const isCrossProvider = srcProvider && tgtProvider && srcProvider !== tgtProvider;

    return {
      id: `${e.source}-${e.target}`,
      source: String(e.source),
      target: String(e.target),
      label: `${e.shared_entity_count} shared`,
      data: { shared_entity_names: e.shared_entity_names || [], shared_entity_count: e.shared_entity_count },
      style: {
        strokeWidth: Math.min(2 + (e.shared_entity_count ?? 1) * 0.5, 8),
        stroke: isCrossProvider ? '#f59e0b' : 'var(--text-secondary)',
        strokeDasharray: isCrossProvider ? '6 3' : undefined,
        opacity: 0.7,
      },
      markerEnd: { type: MarkerType.ArrowClosed, color: isCrossProvider ? '#f59e0b' : 'var(--text-secondary)' },
      labelStyle: { fill: 'var(--text-primary)', fontWeight: 600, fontSize: 11 },
      labelBgStyle: { fill: 'var(--bg-secondary)', fillOpacity: 0.9, rx: 4, ry: 4 },
      labelBgPadding: [8, 4],
      labelShowBg: true,
    };
  });

  return { rfNodes, rfEdges };
};

const buildSharedGraph = (nodes, edges) => {
  const cols = Math.ceil(Math.sqrt(nodes.length)) || 1;
  const rfNodes = nodes.map((n, idx) => ({
    id: n.id,
    position: { x: (idx % cols) * 200, y: Math.floor(idx / cols) * 140 },
    data: { label: n.entity_name, type: n.entity_type },
    style: {
      background: entityColor(n.entity_type),
      color: '#fff',
      border: 'none',
      borderRadius: 8,
      fontSize: 11,
      fontWeight: 600,
      padding: '6px 12px',
      minWidth: 90,
      boxShadow: '0 2px 6px rgba(0,0,0,.2)',
    },
  }));

  const rfEdges = edges.map(e => ({
    id: e.id,
    source: e.source_node_id,
    target: e.target_node_id,
    label: e.relationship_type,
    data: { context: e.context },
    style: { stroke: 'var(--text-secondary)', strokeWidth: 1.5 },
    markerEnd: { type: MarkerType.ArrowClosed, color: 'var(--text-secondary)' },
    labelStyle: { fill: 'var(--text-secondary)', fontWeight: 600, fontSize: 10 },
    labelBgStyle: { fill: 'var(--bg-secondary)', fillOpacity: 0.85 },
  }));

  return { rfNodes, rfEdges };
};

// ── Document Card Component (for Compare View) ─────────────────────────────

const DocumentCard = ({ doc, label, accentColor }) => {
  if (!doc) return null;

  const fileName = getNodeDisplayName(doc);
  const description = doc.description || doc.summary || null;
  const provider = doc.provider || null;
  const status = doc.processing_status || null;
  const createdDate = formatDate(doc.created_date || doc.upload_date);
  const edges = doc.total_edges_contributed ?? null;

  const statusColors = {
    processed: { bg: 'rgba(16,185,129,.12)', color: '#15803d' },
    completed: { bg: 'rgba(16,185,129,.12)', color: '#15803d' },
    indexed: { bg: 'rgba(16,185,129,.12)', color: '#15803d' },
    pending: { bg: 'rgba(245,158,11,.12)', color: '#a16207' },
    failed: { bg: 'rgba(239,68,68,.12)', color: '#b91c1c' },
    error: { bg: 'rgba(239,68,68,.12)', color: '#b91c1c' },
  };
  const statusStyle = statusColors[(status || '').toLowerCase()] || { bg: 'var(--bg-tertiary)', color: 'var(--text-secondary)' };

  return (
    <div className="dbp-doc-card" style={{ borderTopColor: accentColor }}>
      <div className="dbp-doc-card-header">
        <div className="dbp-doc-card-label" style={{ color: accentColor }}>{label}</div>
        <i className="fas fa-file-pdf dbp-doc-card-icon" style={{ color: accentColor }}></i>
      </div>

      <div className="dbp-doc-card-name" title={doc.file_name || fileName}>
        {fileName}
      </div>

      {description && (
        <p className="dbp-doc-card-desc">{description.length > 120 ? description.slice(0, 117) + '…' : description}</p>
      )}

      <div className="dbp-doc-card-meta">
        {provider && (
          <div className="dbp-doc-card-meta-row">
            <i className="fas fa-building dbp-doc-card-meta-icon"></i>
            <span>{provider}</span>
          </div>
        )}
        {status && (
          <div className="dbp-doc-card-meta-row">
            <i className="fas fa-circle-dot dbp-doc-card-meta-icon"></i>
            <span className="dbp-doc-card-status" style={{ background: statusStyle.bg, color: statusStyle.color }}>
              {status}
            </span>
          </div>
        )}
        {createdDate && (
          <div className="dbp-doc-card-meta-row">
            <i className="fas fa-calendar dbp-doc-card-meta-icon"></i>
            <span>{createdDate}</span>
          </div>
        )}
        {edges !== null && (
          <div className="dbp-doc-card-meta-row">
            <i className="fas fa-diagram-project dbp-doc-card-meta-icon"></i>
            <span>{edges} relationships</span>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Sub-components ─────────────────────────────────────────────────────────

const EdgeDetail = ({ edge, nodes, onEntityClick, onClose }) => {
  if (!edge) return null;
  const src = nodes.find(n => n.id === edge.source);
  const tgt = nodes.find(n => n.id === edge.target);
  const sharedNames = edge.data?.shared_entity_names || [];
  return (
    <div className="dng-sidebar">
      <div className="dng-sidebar-header">
        <h4>Connection Details</h4>
        <button className="dng-close-btn" onClick={onClose}><i className="fas fa-times"></i></button>
      </div>
      <p className="dng-edge-docs">
        <strong>{src?.data?.fullLabel || src?.data?.label}</strong>
        <i className="fas fa-arrows-left-right dng-edge-icon"></i>
        <strong>{tgt?.data?.fullLabel || tgt?.data?.label}</strong>
      </p>
      <div className="dng-shared-entities">
        <h5 className="dng-conn-label" style={{ margin: '0 0 10px' }}>Shared Entities ({sharedNames.length})</h5>
        {sharedNames.length === 0 ? (
          <p className="dng-empty">No shared entity names available.</p>
        ) : (
          sharedNames.map((name, i) => (
            <button
              key={i}
              className="dng-entity-tag"
              onClick={() => onEntityClick && onEntityClick(name)}
            >
              {name}
            </button>
          ))
        )}
      </div>
    </div>
  );
};

const NodeDetail = ({ node, allEdges, allNodes, onClose, onCompare }) => {
  const [showLatent, setShowLatent] = useState(false);

  // useMemo must be called before any conditional return (Rules of Hooks)
  const latentConnections = useMemo(
    () => node ? computeLatentConnections(node.id, allEdges, allNodes) : [],
    [node, allEdges, allNodes]
  );

  if (!node) return null;
  const data = node.data || {};
  const connected = allEdges.filter(
    e => e.source === node.id || e.target === node.id
  ).map(e => {
    const otherId = e.source === node.id ? e.target : e.source;
    const other = allNodes.find(n => n.id === otherId);
    return { edge: e, other };
  });

  const sourceIcons = {
    pdf: 'fas fa-file-pdf',
    webpage: 'fas fa-globe',
    web: 'fas fa-globe',
    url: 'fas fa-globe',
    news: 'fas fa-newspaper',
  };
  const sourceIcon = sourceIcons[(data.sourceType || 'pdf').toLowerCase()] || 'fas fa-file-alt';

  return (
    <div className="dng-sidebar">
      <div className="dng-sidebar-header">
        <div className="dng-sidebar-title-wrap">
          <i className={`${sourceIcon} dng-sidebar-source-icon`}></i>
          <h4 title={data.fullLabel}>{data.fullLabel || data.label}</h4>
        </div>
        <button className="dng-close-btn" onClick={onClose}><i className="fas fa-times"></i></button>
      </div>

      <div className="dng-sidebar-actions" style={{ borderTop: 'none', borderBottom: '1px solid var(--border-color)', paddingBottom: 16 }}>
        <button className="btn-primary" style={{ width: '100%', justifyContent: 'center' }} onClick={() => onCompare(node)}>
          <i className="fas fa-code-merge"></i> Compare Document
        </button>
      </div>

      {data.provider && (
        <div className="dng-node-provider">
          <span className="dng-provider-chip" style={{ background: getProviderColor(data.provider) }}>
            {data.provider}
          </span>
        </div>
      )}

      <div className="dng-doc-meta">
        {data.processingStatus && (
          <div className="dng-meta-row">
            <span className="dng-meta-key">Status</span>
            <span className={`dng-meta-status dng-status-${data.processingStatus.toLowerCase()}`}>
              {data.processingStatus}
            </span>
          </div>
        )}
        {data.totalEdges !== null && (
          <div className="dng-meta-row">
            <span className="dng-meta-key">Relationships</span>
            <span className="dng-meta-val">{data.totalEdges}</span>
          </div>
        )}
        {formatDate(data.createdDate) && (
          <div className="dng-meta-row">
            <span className="dng-meta-key">Added</span>
            <span className="dng-meta-val">{formatDate(data.createdDate)}</span>
          </div>
        )}
      </div>

      {data.description && (
        <div className="dng-doc-description">
          <h5 className="dng-section-label"><i className="fas fa-align-left"></i> Description</h5>
          <p>{data.description}</p>
        </div>
      )}

      {connected.length > 0 ? (
        <>
          <h5 className="dng-conn-label">Connected documents ({connected.length})</h5>
          <div className="dng-conn-list">
            {connected.map(({ edge, other }, i) => (
              <div key={i} className="dng-conn-row">
                <span className="dng-conn-name">{other?.data?.fullLabel || other?.data?.label}</span>
                <span className="dng-shared-count">{edge.label}</span>
              </div>
            ))}
          </div>
        </>
      ) : (
        <p className="dng-empty" style={{ padding: '12px 16px', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
          This document has no shared entity connections.
        </p>
      )}

      {/* ── Latent Connections Section ── */}
      <div className="dng-latent-section">
        <button
          className="dng-latent-toggle"
          onClick={() => setShowLatent(v => !v)}
        >
          <i className={`fas fa-chevron-${showLatent ? 'up' : 'down'} dng-latent-chevron`}></i>
          <i className="fas fa-route dng-latent-icon"></i>
          Latent 2-hop Connections
          <span className="dng-latent-count-badge">
            {latentConnections.length > 0 ? latentConnections.length : '0'}
          </span>
        </button>

        {showLatent && (
          <div className="dng-latent-body">
            {latentConnections.length === 0 ? (
              <p className="dng-latent-empty">
                No 2-hop connections found in the current graph view.
              </p>
            ) : (
              <>
                <p className="dng-latent-hint">
                  Documents not directly linked, but reachable via shared intermediary documents:
                </p>
                {latentConnections.map(({ node: ln, sharedIntermediaryCount, sharedIntermediaryNames }, i) => (
                  <div key={i} className="dng-latent-item">
                    <div className="dng-latent-item-left">
                      <div
                        className="dng-latent-item-dot"
                        style={{ background: getProviderColor(ln.data?.provider) }}
                      ></div>
                      <div className="dng-latent-item-info">
                        <span className="dng-latent-item-name">
                          {ln.data?.fullLabel || ln.data?.label}
                        </span>
                        {ln.data?.provider && (
                          <span className="dng-latent-item-provider">{ln.data.provider}</span>
                        )}
                      </div>
                    </div>
                    <div className="dng-latent-item-right">
                      <span className="dng-latent-item-via">
                        via <strong>{sharedIntermediaryNames[0]}</strong>
                        {sharedIntermediaryCount > 1 && (
                          <span className="dng-latent-more"> +{sharedIntermediaryCount - 1}</span>
                        )}
                      </span>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        )}
      </div>

      {data.url && (
        <div className="dng-sidebar-actions">
          <a href={data.url} target="_blank" rel="noopener noreferrer" className="dng-open-doc-btn">
            <i className="fas fa-external-link-alt"></i> Open Document
          </a>
        </div>
      )}
    </div>
  );
};

// ── Provider Overlap Matrix ────────────────────────────────────────────────

const ProviderOverlapMatrix = ({ rfNodes, rfEdges }) => {
  const { providerStats, pairs, maxPairCount } = useMemo(
    () => computeProviderOverlap(rfNodes, rfEdges),
    [rfNodes, rfEdges]
  );

  const providers = Object.keys(providerStats);
  const totalCross = pairs.reduce((s, p) => s + p.count, 0);
  const topPair = pairs[0];

  if (providers.length === 0) {
    return (
      <div className="dng-overlap-matrix">
        <p className="dng-latent-empty" style={{ padding: 16 }}>Load the graph to see provider overlap.</p>
      </div>
    );
  }

  return (
    <div className="dng-overlap-matrix">
      {/* Provider stat chips */}
      <div className="dng-overlap-provider-strip">
        {providers.map(p => (
          <div key={p} className="dng-overlap-provider-chip">
            <span className="dng-overlap-provider-dot" style={{ background: providerStats[p].color }}></span>
            <div className="dng-overlap-provider-info">
              <span className="dng-overlap-provider-name">{p}</span>
              <span className="dng-overlap-provider-stats">
                {providerStats[p].docCount} docs
                {providerStats[p].crossConnections > 0 && ` · ${providerStats[p].crossConnections} cross-links`}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Cross-provider pair bars */}
      <div className="dng-overlap-pairs-section">
        <div className="dng-overlap-pairs-header">
          <span><i className="fas fa-arrows-left-right"></i> Cross-Provider Links</span>
          <span className="dng-overlap-total-badge">{totalCross} total shared entities</span>
        </div>

        {pairs.length === 0 ? (
          <p className="dng-latent-empty" style={{ padding: '8px 0' }}>
            No cross-provider document connections at current threshold.
          </p>
        ) : (
          pairs.slice(0, 8).map((pair, i) => (
            <div key={i} className="dng-overlap-pair-row">
              <div className="dng-overlap-pair-labels">
                <span
                  className="dng-overlap-pair-pill"
                  style={{ background: getProviderColor(pair.p1) }}
                  title={pair.p1}
                >
                  {pair.p1.length > 14 ? pair.p1.slice(0, 12) + '…' : pair.p1}
                </span>
                <i className="fas fa-arrows-left-right dng-overlap-arrow-icon"></i>
                <span
                  className="dng-overlap-pair-pill"
                  style={{ background: getProviderColor(pair.p2) }}
                  title={pair.p2}
                >
                  {pair.p2.length > 14 ? pair.p2.slice(0, 12) + '…' : pair.p2}
                </span>
              </div>
              <div className="dng-overlap-bar-track">
                <div
                  className="dng-overlap-bar-fill"
                  style={{ width: `${maxPairCount > 0 ? (pair.count / maxPairCount) * 100 : 0}%` }}
                ></div>
              </div>
              <span className="dng-overlap-pair-count">{pair.count}</span>
            </div>
          ))
        )}
      </div>

      {/* Redundancy insight */}
      {topPair && topPair.count >= 5 && (
        <div className="dng-overlap-insight">
          <i className="fas fa-lightbulb dng-overlap-insight-icon"></i>
          <span>
            <strong>{topPair.p1}</strong> × <strong>{topPair.p2}</strong> share{' '}
            {topPair.count} cross-document entity links — potential intelligence redundancy.
          </span>
        </div>
      )}
    </div>
  );
};

// ── Comparison Sidebar ─────────────────────────────────────────────────────

const CompareSidebar = ({ doc1, doc2, result, loading, error, onSelectDoc2, pdfList, onClose, onOpenEntity }) => {
  const [rfNodes, setRfNodes] = useState([]);
  const [rfEdges, setRfEdges] = useState([]);

  useEffect(() => {
    if (result?.sharedGraph) {
      const { rfNodes, rfEdges } = buildSharedGraph(
        result.sharedGraph.nodes || [],
        result.sharedGraph.edges || []
      );
      setRfNodes(rfNodes);
      setRfEdges(rfEdges);
    }
  }, [result]);

  const sharedCount = result?.stats?.shared_entity_count ?? 0;

  return (
    <div className="dng-sidebar dng-compare-sidebar">
      <div className="dng-sidebar-header">
        <h4><i className="fas fa-code-merge"></i> Comparison</h4>
        <button className="dng-close-btn" onClick={onClose}><i className="fas fa-times"></i></button>
      </div>

      <div className="dng-compare-content">
        <div className="dbp-card-compare" style={{ gridTemplateColumns: '1fr', padding: '16px', gap: '12px' }}>
          <DocumentCard doc={doc1?.data || doc1} label="Document A" accentColor="#3b82f6" />

          <div className="dbp-card-compare-divider" style={{ flexDirection: 'row', padding: '8px 0' }}>
            <div className="dbp-card-compare-line" style={{ width: 'auto', flex: 1, height: 1 }}></div>
            <div className="dbp-card-compare-badge" style={{ width: 40, height: 40, fontSize: '0.9rem' }}>
              <span>{loading ? '…' : sharedCount}</span>
            </div>
            <div className="dbp-card-compare-line" style={{ width: 'auto', flex: 1, height: 1 }}></div>
          </div>

          {doc2 ? (
            <DocumentCard doc={doc2?.data || doc2} label="Document B" accentColor="#8b5cf6" />
          ) : (
            <div className="dng-compare-placeholder">
              <p>Select a second document from the graph or list</p>
              <select
                className="dbp-selector"
                onChange={(e) => onSelectDoc2(e.target.value ? JSON.parse(e.target.value) : null)}
                style={{ width: '100%', marginTop: 8 }}
              >
                <option value="">— Choose Document —</option>
                {pdfList.filter(p => p.id !== doc1?.id).map(p => (
                  <option key={p.id} value={JSON.stringify(p)}>{getPdfDisplayName(p)}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {error && <div className="dng-error">{error}</div>}

        {result && (
          <div className="dng-shared-graph-wrap" style={{ flex: 1, minHeight: 300, borderTop: '1px solid var(--border-color)' }}>
            <ReactFlowProvider>
              <ReactFlow
                nodes={rfNodes}
                edges={rfEdges}
                fitView
                proOptions={{ hideAttribution: true }}
              >
                <Background color="var(--border-color)" gap={20} />
                <Controls showInteractive={false} />
              </ReactFlow>
            </ReactFlowProvider>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Inner Graph component ──────────────────────────────────────────────────

const NetworkGraphInner = ({
  rfNodes: initNodes, rfEdges: initEdges,
  onEdgeClick, onNodeClick, onPaneClick,
  highlightedNodeId, secondaryNodeId,
}) => {
  const [nodes, setNodes, onNodesChange] = useNodesState(initNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initEdges);
  const [hoveredEdgeId, setHoveredEdgeId] = useState(null);

  useEffect(() => { setNodes(initNodes); }, [initNodes, setNodes]);
  useEffect(() => { setEdges(initEdges); }, [initEdges, setEdges]);

  const styledNodes = nodes.map(n => ({
    ...n,
    style: {
      ...n.style,
      outline: n.id === highlightedNodeId
        ? '3px solid #3b82f6'
        : n.id === secondaryNodeId
          ? '3px dashed #8b5cf6'
          : 'none',
      opacity: (highlightedNodeId && secondaryNodeId && n.id !== highlightedNodeId && n.id !== secondaryNodeId) ? 0.3 : 1,
    },
  }));

  const styledEdges = edges.map(e => ({
    ...e,
    style: {
      ...e.style,
      stroke: e.id === hoveredEdgeId ? '#facc15' : e.style?.stroke,
      zIndex: e.id === hoveredEdgeId ? 10 : 1,
    },
    labelStyle: {
      ...e.labelStyle,
      opacity: 1,
    },
  }));

  return (
    <ReactFlow
      nodes={styledNodes}
      edges={styledEdges}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      onNodeClick={(_, n) => onNodeClick(n)}
      onEdgeClick={(_, e) => onEdgeClick(e)}
      onPaneClick={onPaneClick}
      onEdgeMouseEnter={(_, e) => setHoveredEdgeId(e.id)}
      onEdgeMouseLeave={() => setHoveredEdgeId(null)}
      fitView
      deleteKeyCode={null}
    >
      <Background color="var(--border-color)" gap={20} />
      <Controls />
    </ReactFlow>
  );
};

// ── Main Component ─────────────────────────────────────────────────────────

const DocumentNetworkGraph = ({ onEntityClick }) => {
  // ── Raw API data (not filtered/transformed yet) ──
  const [rawNodes, setRawNodes] = useState([]);
  const [rawEdges, setRawEdges] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [minShared, setMinShared] = useState(2);

  // ── Display controls ──
  const [hideDisconnected, setHideDisconnected] = useState(false);
  const [groupByProvider, setGroupByProvider] = useState(false);
  const [showOverlapMatrix, setShowOverlapMatrix] = useState(false);

  // ── Selection & Compare State ──
  const [selectedNode, setSelectedNode] = useState(null);
  const [selectedEdge, setSelectedEdge] = useState(null);
  const [compareMode, setCompareMode] = useState(false);
  const [compareTarget, setCompareTarget] = useState(null);
  const [compareResult, setCompareResult] = useState(null);
  const [pdfList, setPdfList] = useState([]);

  const sliderRef = useRef(null);

  // ── Derived: count of isolated docs (for the badge on the toggle) ──
  const disconnectedCount = useMemo(() => {
    const connectedIds = new Set();
    rawEdges.forEach(e => {
      connectedIds.add(String(e.source));
      connectedIds.add(String(e.target));
    });
    return rawNodes.filter(n => !connectedIds.has(String(n.id))).length;
  }, [rawNodes, rawEdges]);

  // ── Derived: unique providers for the legend ──
  const providerList = useMemo(() => {
    const seen = new Set();
    rawNodes.forEach(n => { if (n.provider) seen.add(n.provider); });
    return [...seen].sort();
  }, [rawNodes]);

  // ── Derived graph elements (apply filters and layout) ──
  const { rfNodes, rfEdges } = useMemo(() => {
    let filteredNodes = rawNodes;

    if (hideDisconnected) {
      const connectedIds = new Set();
      rawEdges.forEach(e => {
        connectedIds.add(String(e.source));
        connectedIds.add(String(e.target));
      });
      filteredNodes = rawNodes.filter(n => connectedIds.has(String(n.id)));
    }

    return buildNetworkElements(filteredNodes, rawEdges, groupByProvider);
  }, [rawNodes, rawEdges, hideDisconnected, groupByProvider]);

  // ── Load Graph ──
  const loadNetwork = useCallback(async (min) => {
    setLoading(true);
    setError(null);
    setSelectedNode(null);
    setSelectedEdge(null);
    try {
      const res = await fetchDocumentNetwork(min);
      if (res.success) {
        setRawNodes(res.data?.nodes || []);
        setRawEdges(res.data?.edges || []);
      } else {
        setError(res.message || 'Failed to load document network.');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Load PDF list for dropdown fallback ──
  useEffect(() => {
    fetchPdfList(1000).then(res => {
      if (res.success) setPdfList(res.data || []);
    });
  }, []);

  useEffect(() => { loadNetwork(minShared); }, [loadNetwork, minShared]);

  // ── Comparison Logic ──
  const startComparison = (node) => {
    setCompareMode(true);
    setSelectedNode(node);
    setCompareTarget(null);
    setCompareResult(null);
    setSelectedEdge(null);
  };

  const handleNodeClick = (node) => {
    if (compareMode) {
      if (node.id === selectedNode?.id) return;
      setCompareTarget(node);
      fetchComparison(selectedNode, node);
    } else {
      setSelectedNode(node);
      setSelectedEdge(null);
    }
  };

  const fetchComparison = async (doc1, doc2) => {
    if (!doc1 || !doc2) return;
    setLoading(true);
    try {
      const res = await fetchDocumentCommonality(doc1.id, doc2.id);
      if (res.success) {
        setCompareResult(res.data);
      } else {
        setError(res.message);
      }
    } catch {
      setError('Comparison failed.');
    } finally {
      setLoading(false);
    }
  };

  const closeSidebar = () => {
    setSelectedNode(null);
    setSelectedEdge(null);
    setCompareMode(false);
    setCompareTarget(null);
    setCompareResult(null);
  };

  return (
    <div className="dng-container">
      {/* ── Toolbar ── */}
      <div className="dng-toolbar dng-toolbar-enhanced">
        <h3><i className="fas fa-circle-nodes"></i> Document Network</h3>

        <div className="dng-toolbar-right">
          {/* Min shared slider */}
          <div className="dng-control-group">
            <label>Min shared: <strong>{minShared}</strong></label>
            <input
              ref={sliderRef}
              type="range"
              min={1}
              max={10}
              value={minShared}
              onChange={e => setMinShared(parseInt(e.target.value))}
              onMouseUp={() => loadNetwork(minShared)}
              onTouchEnd={() => loadNetwork(minShared)}
              className="dng-slider"
            />
          </div>

          {/* Feature toggles */}
          <div className="dng-toggle-group">
            <button
              className={`dng-toggle-btn ${hideDisconnected ? 'active' : ''}`}
              onClick={() => setHideDisconnected(v => !v)}
              title={hideDisconnected ? 'Show isolated documents' : 'Hide isolated documents'}
            >
              <i className={`fas fa-${hideDisconnected ? 'eye' : 'eye-slash'}`}></i>
              Hide Isolated
              {disconnectedCount > 0 && (
                <span className={`dng-toggle-badge ${hideDisconnected ? 'dng-toggle-badge-active' : ''}`}>
                  {disconnectedCount}
                </span>
              )}
            </button>

            <button
              className={`dng-toggle-btn ${groupByProvider ? 'active' : ''}`}
              onClick={() => setGroupByProvider(v => !v)}
              title="Cluster documents by their source provider"
            >
              <i className="fas fa-layer-group"></i>
              Group by Provider
            </button>

            <button
              className={`dng-toggle-btn ${showOverlapMatrix ? 'active' : ''}`}
              onClick={() => setShowOverlapMatrix(v => !v)}
              title="Show cross-provider intelligence overlap"
            >
              <i className="fas fa-table-cells"></i>
              Provider Overlap
            </button>
          </div>
        </div>
      </div>

      {/* ── Provider Legend (visible when groupByProvider is on) ── */}
      {groupByProvider && providerList.length > 0 && (
        <div className="dng-provider-legend">
          <span className="dng-provider-legend-label">
            <i className="fas fa-layer-group"></i> Grouped by provider:
          </span>
          {providerList.map(p => (
            <span key={p} className="dng-provider-legend-chip">
              <span className="dng-provider-legend-dot" style={{ background: getProviderColor(p) }}></span>
              {p}
            </span>
          ))}
          <span className="dng-provider-legend-hint">
            <i className="fas fa-circle" style={{ color: '#f59e0b', fontSize: '0.6rem' }}></i>
            Dashed amber = cross-provider link
          </span>
        </div>
      )}

      {/* ── Provider Overlap Matrix (inline panel) ── */}
      {showOverlapMatrix && (
        <div className="dng-overlap-wrap">
          <div className="dng-overlap-header">
            <span><i className="fas fa-table-cells"></i> Provider Intelligence Overlap</span>
            <button className="dng-close-btn" onClick={() => setShowOverlapMatrix(false)}>
              <i className="fas fa-times"></i>
            </button>
          </div>
          <ProviderOverlapMatrix rfNodes={rfNodes} rfEdges={rfEdges} />
        </div>
      )}

      {error && !compareMode && (
        <div className="dng-error">
          <i className="fas fa-exclamation-triangle"></i> {error}
          <button className="btn-secondary" onClick={() => loadNetwork(minShared)} style={{ marginLeft: 12 }}>Retry</button>
        </div>
      )}

      <div className="dng-layout">
        <div className="dng-graph-area">
          {loading && !compareResult ? (
            <div className="dng-loading">
              <div className="dng-spinner"></div>
              <p>Loading document network…</p>
            </div>
          ) : rfNodes.length === 0 ? (
            <div className="dng-empty-state">
              <i className="fas fa-circle-nodes dng-empty-icon"></i>
              <h3>No document connections found</h3>
              <p>
                {hideDisconnected && disconnectedCount > 0
                  ? `${disconnectedCount} isolated document${disconnectedCount > 1 ? 's' : ''} hidden. Toggle "Hide Isolated" off to view them.`
                  : 'Try lowering the minimum shared entities threshold.'}
              </p>
            </div>
          ) : (
            <ReactFlowProvider>
              <NetworkGraphInner
                rfNodes={rfNodes}
                rfEdges={rfEdges}
                onNodeClick={handleNodeClick}
                onEdgeClick={(e) => {
                  // Only respond to edge clicks when NOT in compare mode.
                  // Do NOT clear selectedNode in compare mode — that would break the
                  // compare workflow and leave the UI in a stuck state.
                  if (!compareMode) {
                    setSelectedNode(null);
                    setSelectedEdge(e);
                  }
                }}
                onPaneClick={() => {
                  // Clicking the graph background always fully resets selection &
                  // compare mode so the user can start fresh.
                  if (compareMode || selectedNode || selectedEdge) {
                    closeSidebar();
                  }
                }}
                highlightedNodeId={selectedNode?.id}
                secondaryNodeId={compareTarget?.id}
              />
            </ReactFlowProvider>
          )}
        </div>

        {/* ── Sidebar Logic ── */}
        {(selectedNode || selectedEdge) && (
          <div className={`dng-sidebar-wrap ${compareMode ? 'dng-sidebar-wide' : ''}`}>
            {compareMode ? (
              <CompareSidebar
                doc1={selectedNode}
                doc2={compareTarget}
                result={compareResult}
                loading={loading}
                error={error}
                onSelectDoc2={(d) => {
                  setCompareTarget(d);
                  fetchComparison(selectedNode, d);
                }}
                pdfList={pdfList}
                onClose={closeSidebar}
                onOpenEntity={onEntityClick}
              />
            ) : selectedNode ? (
              <NodeDetail
                node={selectedNode}
                allEdges={rfEdges}
                allNodes={rfNodes}
                onClose={closeSidebar}
                onEntityClick={onEntityClick}
                onCompare={startComparison}
              />
            ) : selectedEdge ? (
              <EdgeDetail
                edge={selectedEdge}
                nodes={rfNodes}
                onEntityClick={onEntityClick}
                onClose={closeSidebar}
              />
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentNetworkGraph;
